/**
 * CPU Fallback Compute Engine for Helios
 * Uses Web Workers for parallel computation when no GPU is available.
 */

import type { PositionedNode, ClusterResult } from './webgpu-engine';

export interface CPUCapabilities {
  backend: 'cpu';
  hardwareConcurrency: number;
  deviceMemory: number | null;
}

export class CPUFallback {
  private worker: Worker | null = null;
  private workerReady = false;
  private pendingCallbacks = new Map<
    string,
    { resolve: (value: unknown) => void; reject: (reason: unknown) => void }
  >();

  async init(): Promise<void> {
    try {
      // Try to create Web Worker for force layout
      const workerUrl = new URL('./workers/force-layout.worker.ts', import.meta.url);
      this.worker = new Worker(workerUrl, { type: 'module' });

      this.worker.onmessage = (e: MessageEvent) => {
        const { id, result, error } = e.data;
        const cb = this.pendingCallbacks.get(id);
        if (cb) {
          this.pendingCallbacks.delete(id);
          if (error) cb.reject(new Error(error));
          else cb.resolve(result);
        }
        if (e.data.type === 'ready') {
          this.workerReady = true;
        }
      };

      this.worker.onerror = (e) => {
        console.warn('[CPU] Worker error, falling back to main thread:', e.message);
        this.worker = null;
        this.workerReady = false;
      };
    } catch {
      console.warn('[CPU] Web Worker not available, using main thread');
      this.worker = null;
    }

    console.log(
      `[CPU] Fallback engine initialized (workers: ${this.worker ? 'yes' : 'no'}, cores: ${navigator.hardwareConcurrency || 'unknown'})`
    );
  }

  private sendToWorker(type: string, payload: unknown, transferables?: Transferable[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.workerReady) {
        reject(new Error('Worker not available'));
        return;
      }
      const id = `${type}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      this.pendingCallbacks.set(id, { resolve, reject });
      this.worker.postMessage({ id, type, payload }, transferables ?? []);
    });
  }

  async computeForceLayout(
    nodes: Array<{ id: string; x?: number; y?: number; mass?: number }>,
    edges: Array<{ source: number; target: number }>,
    iterations: number = 100,
    options?: {
      repulsion?: number;
      attraction?: number;
      damping?: number;
      gravity?: number;
    }
  ): Promise<PositionedNode[]> {
    const nodeCount = nodes.length;
    if (nodeCount === 0) return [];

    // Try worker first
    if (this.worker) {
      try {
        const result = (await this.sendToWorker('forceLayout', {
          nodes,
          edges,
          iterations,
          options,
        })) as PositionedNode[];
        return result;
      } catch {
        // Fall through to main thread
      }
    }

    // Main thread fallback
    return this.computeForceLayoutMainThread(nodes, edges, iterations, options);
  }

  private computeForceLayoutMainThread(
    nodes: Array<{ id: string; x?: number; y?: number; mass?: number }>,
    edges: Array<{ source: number; target: number }>,
    iterations: number,
    options?: {
      repulsion?: number;
      attraction?: number;
      damping?: number;
      gravity?: number;
    }
  ): PositionedNode[] {
    const nodeCount = nodes.length;
    const repulsion = options?.repulsion ?? 500;
    const attraction = options?.attraction ?? 0.01;
    const damping = options?.damping ?? 0.9;
    const gravity = options?.gravity ?? 0.1;
    const deltaT = 0.1;

    // Initialize positions
    const px = new Float32Array(nodeCount);
    const py = new Float32Array(nodeCount);
    const vx = new Float32Array(nodeCount);
    const vy = new Float32Array(nodeCount);

    for (let i = 0; i < nodeCount; i++) {
      if (nodes[i].x !== undefined && nodes[i].y !== undefined) {
        px[i] = nodes[i].x!;
        py[i] = nodes[i].y!;
      } else {
        const angle = (2 * Math.PI * i) / nodeCount;
        const radius = Math.sqrt(nodeCount) * 15;
        px[i] = Math.cos(angle) * radius;
        py[i] = Math.sin(angle) * radius;
      }
    }

    for (let iter = 0; iter < iterations; iter++) {
      // Compute forces for each node
      for (let i = 0; i < nodeCount; i++) {
        let fx = 0;
        let fy = 0;

        // Repulsion (Barnes-Hut simplified: O(n^2) for CPU)
        for (let j = 0; j < nodeCount; j++) {
          if (j === i) continue;
          const dx = px[i] - px[j];
          const dy = py[i] - py[j];
          const distSq = Math.max(dx * dx + dy * dy, 0.01);
          const dist = Math.sqrt(distSq);
          const force = repulsion / distSq;
          fx += (dx / dist) * force;
          fy += (dy / dist) * force;
        }

        // Attraction along edges
        for (const edge of edges) {
          let neighborIdx = -1;
          if (edge.source === i) neighborIdx = edge.target;
          else if (edge.target === i) neighborIdx = edge.source;
          if (neighborIdx < 0) continue;

          const dx = px[neighborIdx] - px[i];
          const dy = py[neighborIdx] - py[i];
          const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 0.01);
          const force = attraction * (dist - 50);
          fx += (dx / dist) * force;
          fy += (dy / dist) * force;
        }

        // Gravity
        const centerDist = Math.max(Math.sqrt(px[i] * px[i] + py[i] * py[i]), 0.01);
        fx += gravity * (-px[i] / centerDist) * centerDist;
        fy += gravity * (-py[i] / centerDist) * centerDist;

        // Update velocity
        vx[i] = (vx[i] + fx * deltaT) * damping;
        vy[i] = (vy[i] + fy * deltaT) * damping;

        // Clamp velocity
        const speed = Math.sqrt(vx[i] * vx[i] + vy[i] * vy[i]);
        if (speed > 50) {
          vx[i] *= 50 / speed;
          vy[i] *= 50 / speed;
        }
      }

      // Update positions
      for (let i = 0; i < nodeCount; i++) {
        px[i] += vx[i] * deltaT;
        py[i] += vy[i] * deltaT;
      }
    }

    return nodes.map((n, i) => ({
      id: n.id,
      x: px[i],
      y: py[i],
    }));
  }

  async computeEmbeddingSimilarity(
    vectorsA: Float32Array,
    vectorsB: Float32Array,
    dim: number
  ): Promise<Float32Array> {
    const rowsA = vectorsA.length / dim;
    const rowsB = vectorsB.length / dim;
    const result = new Float32Array(rowsA * rowsB);

    for (let i = 0; i < rowsA; i++) {
      for (let j = 0; j < rowsB; j++) {
        let dot = 0;
        let normA = 0;
        let normB = 0;
        const offA = i * dim;
        const offB = j * dim;

        for (let d = 0; d < dim; d++) {
          const a = vectorsA[offA + d];
          const b = vectorsB[offB + d];
          dot += a * b;
          normA += a * a;
          normB += b * b;
        }

        const denom = Math.sqrt(normA) * Math.sqrt(normB);
        result[i * rowsB + j] = denom > 0.0001 ? dot / denom : 0;
      }
    }

    return result;
  }

  async computeHeatmap(
    data: Float32Array,
    width: number,
    height: number
  ): Promise<Uint8ClampedArray> {
    const pixelCount = width * height;
    let minVal = Infinity;
    let maxVal = -Infinity;
    for (let i = 0; i < data.length; i++) {
      if (data[i] < minVal) minVal = data[i];
      if (data[i] > maxVal) maxVal = data[i];
    }

    const range = maxVal - minVal;
    const rgba = new Uint8ClampedArray(pixelCount * 4);

    for (let i = 0; i < pixelCount; i++) {
      const t = range > 0.0001 ? (data[i] - minVal) / range : 0;
      const r = Math.max(0, Math.min(1, 1.5 - Math.abs(t * 4 - 3)));
      const g = Math.max(0, Math.min(1, 1.5 - Math.abs(t * 4 - 2)));
      const b = Math.max(0, Math.min(1, 1.5 - Math.abs(t * 4 - 1)));
      rgba[i * 4 + 0] = r * 255;
      rgba[i * 4 + 1] = g * 255;
      rgba[i * 4 + 2] = b * 255;
      rgba[i * 4 + 3] = 255;
    }

    return rgba;
  }

  async computeClusterDetection(
    adjacencyMatrix: Float32Array,
    numClusters: number = 4
  ): Promise<ClusterResult[]> {
    const n = Math.round(Math.sqrt(adjacencyMatrix.length));
    if (n * n !== adjacencyMatrix.length) {
      throw new Error('Adjacency matrix must be square');
    }

    // Degree matrix
    const degree = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let j = 0; j < n; j++) sum += adjacencyMatrix[i * n + j];
      degree[i] = sum;
    }

    // Normalized Laplacian
    const laplacian = new Float32Array(n * n);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) {
          laplacian[i * n + j] = degree[i] > 0 ? 1.0 : 0.0;
        } else {
          const dI = degree[i] > 0 ? 1 / Math.sqrt(degree[i]) : 0;
          const dJ = degree[j] > 0 ? 1 / Math.sqrt(degree[j]) : 0;
          laplacian[i * n + j] = -adjacencyMatrix[i * n + j] * dI * dJ;
        }
      }
    }

    // Power iteration for eigenvectors
    const eigenvectors: Float32Array[] = [];
    for (let k = 0; k < Math.min(numClusters, n); k++) {
      let vec = new Float32Array(n);
      for (let i = 0; i < n; i++) vec[i] = Math.random() - 0.5;

      for (const prev of eigenvectors) {
        let dot = 0;
        for (let i = 0; i < n; i++) dot += vec[i] * prev[i];
        for (let i = 0; i < n; i++) vec[i] -= dot * prev[i];
      }

      for (let iter = 0; iter < 20; iter++) {
        const result = new Float32Array(n);
        for (let i = 0; i < n; i++) {
          let sum = 0;
          for (let j = 0; j < n; j++) sum += laplacian[i * n + j] * vec[j];
          result[i] = sum;
        }
        for (const prev of eigenvectors) {
          let dot = 0;
          for (let i = 0; i < n; i++) dot += result[i] * prev[i];
          for (let i = 0; i < n; i++) result[i] -= dot * prev[i];
        }
        let norm = 0;
        for (let i = 0; i < n; i++) norm += result[i] * result[i];
        norm = Math.sqrt(norm);
        if (norm > 1e-10) for (let i = 0; i < n; i++) result[i] /= norm;
        vec = result;
      }
      eigenvectors.push(vec);
    }

    // K-means
    const clusters: ClusterResult[] = [];
    if (eigenvectors.length >= 2) {
      const embedding = eigenvectors.slice(0, numClusters);
      const centroids: number[][] = [];
      for (let k = 0; k < numClusters; k++) {
        const idx = Math.floor((k * n) / numClusters);
        centroids.push(embedding.map((ev) => ev[idx]));
      }

      const assignments = new Int32Array(n);
      for (let iter = 0; iter < 20; iter++) {
        for (let i = 0; i < n; i++) {
          let bestDist = Infinity;
          let bestK = 0;
          for (let k = 0; k < numClusters; k++) {
            let dist = 0;
            for (let d = 0; d < embedding.length; d++) {
              const diff = embedding[d][i] - centroids[k][d];
              dist += diff * diff;
            }
            if (dist < bestDist) { bestDist = dist; bestK = k; }
          }
          assignments[i] = bestK;
        }
        for (let k = 0; k < numClusters; k++) {
          const count = assignments.filter((a) => a === k).length;
          if (count === 0) continue;
          for (let d = 0; d < embedding.length; d++) {
            let sum = 0;
            for (let i = 0; i < n; i++) if (assignments[i] === k) sum += embedding[d][i];
            centroids[k][d] = sum / count;
          }
        }
      }
      for (let i = 0; i < n; i++) clusters.push({ nodeIndex: i, clusterId: assignments[i] });
    } else {
      for (let i = 0; i < n; i++) clusters.push({ nodeIndex: i, clusterId: 0 });
    }

    return clusters;
  }

  getCapabilities(): CPUCapabilities {
    return {
      backend: 'cpu',
      hardwareConcurrency: navigator.hardwareConcurrency || 1,
      deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory ?? null,
    };
  }

  dispose(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingCallbacks.clear();
    this.workerReady = false;
    console.log('[CPU] Fallback engine disposed');
  }
}
